<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { debounce } from '@chatwoot/utils';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CaptainDocumentAPI from 'dashboard/api/captain/document';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';
import DocumentCard from 'dashboard/components-next/captain/assistant/DocumentCard.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import RelatedResponses from 'dashboard/components-next/captain/pageComponents/document/RelatedResponses.vue';
import DocumentContentDialog from 'dashboard/components-next/captain/pageComponents/document/DocumentContentDialog.vue';
import CreateDocumentDialog from 'dashboard/components-next/captain/pageComponents/document/CreateDocumentDialog.vue';
import DocumentPageEmptyState from 'dashboard/components-next/captain/pageComponents/emptyStates/DocumentPageEmptyState.vue';
import FeatureSpotlightPopover from 'dashboard/components-next/feature-spotlight/FeatureSpotlightPopover.vue';
import LimitBanner from 'dashboard/components-next/captain/pageComponents/document/LimitBanner.vue';

const route = useRoute();
const store = useStore();
const { t } = useI18n();

const { isOnChatwootCloud } = useAccount();
const uiFlags = useMapGetter('captainDocuments/getUIFlags');
const documents = useMapGetter('captainDocuments/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const documentsMeta = useMapGetter('captainDocuments/getMeta');

const selectedAssistantId = computed(() => Number(route.params.assistantId));

const selectedDocument = ref(null);
const deleteDocumentDialog = ref(null);

const handleDelete = () => {
  deleteDocumentDialog.value.dialogRef.open();
};

const showRelatedResponses = ref(false);
const showCreateDialog = ref(false);
const createDocumentDialog = ref(null);
const relationQuestionDialog = ref(null);

const handleShowRelatedDocument = () => {
  showRelatedResponses.value = true;
  nextTick(() => relationQuestionDialog.value.dialogRef.open());
};
const handleCreateDocument = () => {
  showCreateDialog.value = true;
  nextTick(() => createDocumentDialog.value.dialogRef.open());
};

const handleRelatedResponseClose = () => {
  showRelatedResponses.value = false;
};

const handleCreateDialogClose = () => {
  showCreateDialog.value = false;
};

const handleExtractContent = async id => {
  try {
    await CaptainDocumentAPI.extractContent(id);
    useAlert(t('CAPTAIN.DOCUMENTS.EXTRACT_CONTENT.STARTED'));
  } catch (error) {
    useAlert(t('CAPTAIN.DOCUMENTS.EXTRACT_CONTENT.ERROR'));
  }
};

const showContentDialog = ref(false);
const contentDialog = ref(null);

const handleShowContent = () => {
  showContentDialog.value = true;
  nextTick(() => contentDialog.value.dialogRef.open());
};

const handleContentDialogClose = () => {
  showContentDialog.value = false;
};

const handleViewFile = () => {
  const url = selectedDocument.value?.display_url;
  if (!url) return;
  // noopener: the document is operator-supplied, so the opened tab must not
  // get a handle back to this one.
  window.open(url, '_blank', 'noopener,noreferrer');
};

const handleAction = ({ action, id }) => {
  selectedDocument.value = documents.value.find(
    captainDocument => id === captainDocument.id
  );

  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    } else if (action === 'viewRelatedQuestions') {
      handleShowRelatedDocument();
    } else if (action === 'extractContent') {
      handleExtractContent(id);
    } else if (action === 'viewContent') {
      handleShowContent();
    } else if (action === 'viewFile') {
      handleViewFile();
    }
  });
};

const searchQuery = ref('');
const documentType = ref('all');
const contentStatus = ref('all');

// Labels resolved here with literal keys: building the key in the template
// hides it from the i18n tooling that checks for missing translations.
const typeFilters = computed(() => [
  { value: 'all', label: t('CAPTAIN.DOCUMENTS.FILTERS.TYPE_OPTIONS.all') },
  { value: 'pdf', label: t('CAPTAIN.DOCUMENTS.FILTERS.TYPE_OPTIONS.pdf') },
  { value: 'link', label: t('CAPTAIN.DOCUMENTS.FILTERS.TYPE_OPTIONS.link') },
]);

const contentFilters = computed(() => [
  { value: 'all', label: t('CAPTAIN.DOCUMENTS.FILTERS.CONTENT_OPTIONS.all') },
  {
    value: 'without',
    label: t('CAPTAIN.DOCUMENTS.FILTERS.CONTENT_OPTIONS.without'),
  },
  { value: 'with', label: t('CAPTAIN.DOCUMENTS.FILTERS.CONTENT_OPTIONS.with') },
]);

// The server reads absent params as "no filter", so 'all' is sent as undefined
// rather than as a value the backend would have to special-case.
const activeFilters = computed(() => ({
  searchKey: searchQuery.value.trim() || undefined,
  assistantId: selectedAssistantId.value || undefined,
  documentType: documentType.value === 'all' ? undefined : documentType.value,
  contentStatus:
    contentStatus.value === 'all' ? undefined : contentStatus.value,
}));

const selectedIds = ref([]);
// Set when the user escalates from "this page" to "everything that matches".
// The ids are never materialised in the browser; the server re-runs the filters.
const selectAllMatching = ref(false);

const clearSelection = () => {
  selectedIds.value = [];
  selectAllMatching.value = false;
};

const fetchDocuments = (page = 1) => {
  clearSelection();
  store.dispatch('captainDocuments/get', { page, ...activeFilters.value });
};

const debouncedSearch = debounce(() => fetchDocuments(1), 500);

const applyFilter = (kind, value) => {
  if (kind === 'type') documentType.value = value;
  else contentStatus.value = value;
  fetchDocuments(1);
};

const totalCount = computed(() => documentsMeta.value?.totalCount || 0);
const pageIds = computed(() => (documents.value || []).map(doc => doc.id));

const selectedCount = computed(() =>
  selectAllMatching.value ? totalCount.value : selectedIds.value.length
);
const hasSelection = computed(() => selectedCount.value > 0);

const allOnPageSelected = computed(
  () =>
    pageIds.value.length > 0 &&
    pageIds.value.every(id => selectedIds.value.includes(id))
);
const someOnPageSelected = computed(
  () => selectedIds.value.length > 0 && !allOnPageSelected.value
);

// Only worth offering once the page is fully ticked and there is more behind it.
const canSelectAllMatching = computed(
  () =>
    !selectAllMatching.value &&
    allOnPageSelected.value &&
    totalCount.value > pageIds.value.length
);

const toggleSelectPage = () => {
  selectAllMatching.value = false;
  selectedIds.value = allOnPageSelected.value ? [] : [...pageIds.value];
};

const handleSelect = ({ id, selected }) => {
  selectAllMatching.value = false;
  if (selected) {
    if (!selectedIds.value.includes(id)) selectedIds.value.push(id);
  } else {
    selectedIds.value = selectedIds.value.filter(current => current !== id);
  }
};

const bulkPayload = computed(() =>
  selectAllMatching.value
    ? { all: true, filters: activeFilters.value }
    : { ids: selectedIds.value }
);

const isBulkRunning = ref(false);
const bulkDeleteDialog = ref(null);

const handleBulkExtract = async () => {
  isBulkRunning.value = true;
  try {
    const { data } = await CaptainDocumentAPI.bulkExtractContent(
      bulkPayload.value
    );
    useAlert(
      data.count
        ? t('CAPTAIN.DOCUMENTS.BULK.EXTRACT_STARTED', { count: data.count })
        : t('CAPTAIN.DOCUMENTS.BULK.NO_PDF_SELECTED')
    );
    clearSelection();
  } catch (error) {
    useAlert(t('CAPTAIN.DOCUMENTS.BULK.ERROR'));
  } finally {
    isBulkRunning.value = false;
  }
};

const confirmBulkDelete = () => bulkDeleteDialog.value.open();

const handleBulkDelete = async () => {
  isBulkRunning.value = true;
  try {
    const { data } = await CaptainDocumentAPI.bulkDestroy(bulkPayload.value);
    useAlert(
      data.queued
        ? t('CAPTAIN.DOCUMENTS.BULK.DELETE_QUEUED', { count: data.count })
        : t('CAPTAIN.DOCUMENTS.BULK.DELETE_DONE', { count: data.count })
    );
    bulkDeleteDialog.value.close();
    fetchDocuments(1);
  } catch (error) {
    useAlert(t('CAPTAIN.DOCUMENTS.BULK.ERROR'));
  } finally {
    isBulkRunning.value = false;
  }
};

const onPageChange = page => fetchDocuments(page);

const onDeleteSuccess = () => {
  if (documents.value?.length === 0 && documentsMeta.value?.page > 1) {
    onPageChange(documentsMeta.value.page - 1);
  }
};

onMounted(() => {
  fetchDocuments();
});
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.DOCUMENTS.HEADER')"
    :button-label="$t('CAPTAIN.DOCUMENTS.ADD_NEW')"
    :button-policy="['administrator']"
    :total-count="documentsMeta.totalCount"
    :current-page="documentsMeta.page"
    :show-pagination-footer="!isFetching && !!documents.length"
    :is-fetching="isFetching"
    :is-empty="!documents.length"
    :show-know-more="false"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
    @update:current-page="onPageChange"
    @click="handleCreateDocument"
  >
    <template #search>
      <Input
        v-model="searchQuery"
        :placeholder="$t('CAPTAIN.DOCUMENTS.SEARCH_PLACEHOLDER')"
        class="w-64"
        size="sm"
        type="search"
        @input="debouncedSearch"
      />
    </template>

    <template #knowMore>
      <FeatureSpotlightPopover
        :button-label="$t('CAPTAIN.HEADER_KNOW_MORE')"
        :title="$t('CAPTAIN.DOCUMENTS.EMPTY_STATE.FEATURE_SPOTLIGHT.TITLE')"
        :note="$t('CAPTAIN.DOCUMENTS.EMPTY_STATE.FEATURE_SPOTLIGHT.NOTE')"
        :hide-actions="!isOnChatwootCloud"
        fallback-thumbnail="/assets/images/dashboard/captain/document-popover-light.svg"
        fallback-thumbnail-dark="/assets/images/dashboard/captain/document-popover-dark.svg"
        learn-more-url="https://chwt.app/captain-document"
      />
    </template>

    <!-- Lives in #controls, not #body: the layout swaps #body for the empty
         state, which would take the filters away exactly when the user needs
         them to undo the filter that emptied the list. -->
    <template #controls>
      <div class="flex flex-col gap-3 mb-4">
        <div class="flex flex-wrap gap-x-6 gap-y-2 items-center">
          <div class="flex gap-1 items-center">
            <span class="text-sm text-n-slate-11">
              {{ $t('CAPTAIN.DOCUMENTS.FILTERS.TYPE') }}
            </span>
            <button
              v-for="option in typeFilters"
              :key="option.value"
              type="button"
              class="px-2 py-1 text-sm rounded-md transition-colors duration-150"
              :class="
                documentType === option.value
                  ? 'bg-n-slate-4 text-n-slate-12'
                  : 'text-n-slate-11 hover:bg-n-slate-3'
              "
              @click="applyFilter('type', option.value)"
            >
              {{ option.label }}
            </button>
          </div>

          <div class="flex gap-1 items-center">
            <span class="text-sm text-n-slate-11">
              {{ $t('CAPTAIN.DOCUMENTS.FILTERS.CONTENT') }}
            </span>
            <button
              v-for="option in contentFilters"
              :key="option.value"
              type="button"
              class="px-2 py-1 text-sm rounded-md transition-colors duration-150"
              :class="
                contentStatus === option.value
                  ? 'bg-n-slate-4 text-n-slate-12'
                  : 'text-n-slate-11 hover:bg-n-slate-3'
              "
              @click="applyFilter('content', option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <div
          v-if="documents.length"
          class="flex flex-wrap gap-x-4 gap-y-2 items-center px-3 py-2 rounded-lg bg-n-alpha-1"
        >
          <label class="flex gap-2 items-center cursor-pointer">
            <Checkbox
              :model-value="allOnPageSelected"
              :indeterminate="someOnPageSelected"
              @change="toggleSelectPage"
            />
            <span class="text-sm text-n-slate-11">
              {{
                hasSelection
                  ? $t('CAPTAIN.DOCUMENTS.BULK.SELECTED', {
                      count: selectedCount,
                    })
                  : $t('CAPTAIN.DOCUMENTS.BULK.SELECT_PAGE')
              }}
            </span>
          </label>

          <button
            v-if="canSelectAllMatching"
            type="button"
            class="text-sm underline text-n-brand hover:no-underline"
            @click="selectAllMatching = true"
          >
            {{
              $t('CAPTAIN.DOCUMENTS.BULK.SELECT_ALL_MATCHING', {
                count: totalCount,
              })
            }}
          </button>

          <template v-if="hasSelection">
            <div class="flex gap-2 items-center ltr:ml-auto rtl:mr-auto">
              <Button
                :label="$t('CAPTAIN.DOCUMENTS.BULK.EXTRACT')"
                icon="i-ph-text-align-left"
                variant="ghost"
                color="slate"
                size="sm"
                :is-loading="isBulkRunning"
                :disabled="isBulkRunning"
                @click="handleBulkExtract"
              />
              <Button
                :label="$t('CAPTAIN.DOCUMENTS.BULK.DELETE')"
                icon="i-lucide-trash"
                variant="ghost"
                color="ruby"
                size="sm"
                :disabled="isBulkRunning"
                @click="confirmBulkDelete"
              />
              <Button
                :label="$t('CAPTAIN.DOCUMENTS.BULK.CLEAR')"
                variant="ghost"
                color="slate"
                size="sm"
                :disabled="isBulkRunning"
                @click="clearSelection"
              />
            </div>
          </template>
        </div>
      </div>
    </template>

    <template #emptyState>
      <DocumentPageEmptyState @click="handleCreateDocument" />
    </template>

    <template #paywall>
      <CaptainPaywall />
    </template>

    <template #body>
      <LimitBanner class="mb-5" />

      <div class="flex flex-col gap-4">
        <DocumentCard
          v-for="doc in documents"
          :id="doc.id"
          :key="doc.id"
          :name="doc.name || doc.external_link"
          :external-link="doc.external_link"
          :assistant="doc.assistant"
          :created-at="doc.created_at"
          :display-url="doc.display_url"
          :content="doc.content"
          selectable
          :selected="selectAllMatching || selectedIds.includes(doc.id)"
          @action="handleAction"
          @select="handleSelect"
        />
      </div>
    </template>

    <Dialog
      ref="bulkDeleteDialog"
      type="alert"
      :title="$t('CAPTAIN.DOCUMENTS.BULK.DELETE_CONFIRM_TITLE')"
      :description="
        $t('CAPTAIN.DOCUMENTS.BULK.DELETE_CONFIRM_BODY', {
          count: selectedCount,
        })
      "
      :confirm-button-label="$t('CAPTAIN.DOCUMENTS.BULK.DELETE')"
      :is-loading="isBulkRunning"
      @confirm="handleBulkDelete"
    />

    <RelatedResponses
      v-if="showRelatedResponses"
      ref="relationQuestionDialog"
      :captain-document="selectedDocument"
      @close="handleRelatedResponseClose"
    />
    <DocumentContentDialog
      v-if="showContentDialog"
      ref="contentDialog"
      :captain-document="selectedDocument"
      @close="handleContentDialogClose"
    />
    <CreateDocumentDialog
      v-if="showCreateDialog"
      ref="createDocumentDialog"
      :assistant-id="selectedAssistantId"
      @close="handleCreateDialogClose"
    />
    <DeleteDialog
      v-if="selectedDocument"
      ref="deleteDocumentDialog"
      :entity="selectedDocument"
      type="Documents"
      @delete-success="onDeleteSuccess"
    />
  </PageLayout>
</template>
